import { useAuth } from '../contexts/AuthContext';
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../api';
import { Plus, Search, Edit2, Trash2, Package, X, QrCode, MapPin, Printer, Clock, CheckCircle, Truck, XCircle, Archive, ArrowLeftRight } from 'lucide-react';

const TAMANHOS_ETIQUETA = [
  { value: 'a4',      label: 'A4',      desc: 'Folha inteira, ideal para impressoras comuns' },
  { value: 'a5',      label: 'A5',      desc: 'Meia folha, ótimo para encomendas médias' },
  { value: 'termica', label: 'Térmica', desc: '100×150mm, etiqueta autocolante pequena' },
];
const FORMAS_PAGAMENTO_ENC = ['Pix', 'Cartão', 'Dinheiro', 'Mista', 'Depósito', 'Cortesia', 'A Receber'];

const STATUS_ENC = [
  { value: 'recebida',    label: 'Recebida',    cls: 'bg-blue-100 text-blue-700' },
  { value: 'em_transito', label: 'Em Trânsito', cls: 'bg-amber-100 text-amber-700' },
  { value: 'entregue',    label: 'Entregue',    cls: 'bg-emerald-100 text-emerald-700' },
  { value: 'cancelada',   label: 'Cancelada',   cls: 'bg-red-100 text-red-700' },
];
const getStatusCls   = (s) => STATUS_ENC.find(o => o.value === s)?.cls   || 'bg-slate-100 text-slate-700';
const getStatusLabel = (s) => STATUS_ENC.find(o => o.value === s)?.label || s;

const maskCpfCnpj = (v) => {
  const n = v.replace(/\D/g, '').slice(0, 14);
  if (n.length <= 11) {
    if (n.length <= 3) return n;
    if (n.length <= 6) return `${n.slice(0,3)}.${n.slice(3)}`;
    if (n.length <= 9) return `${n.slice(0,3)}.${n.slice(3,6)}.${n.slice(6)}`;
    return `${n.slice(0,3)}.${n.slice(3,6)}.${n.slice(6,9)}-${n.slice(9)}`;
  }
  if (n.length <= 2) return n;
  if (n.length <= 5) return `${n.slice(0,2)}.${n.slice(2)}`;
  if (n.length <= 8) return `${n.slice(0,2)}.${n.slice(2,5)}.${n.slice(5)}`;
  if (n.length <= 12) return `${n.slice(0,2)}.${n.slice(2,5)}.${n.slice(5,8)}/${n.slice(8)}`;
  return `${n.slice(0,2)}.${n.slice(2,5)}.${n.slice(5,8)}/${n.slice(8,12)}-${n.slice(12)}`;
};
const maskTel = (v) => {
  const n = v.replace(/\D/g, '').slice(0, 11);
  if (n.length <= 2) return n.length ? `(${n}` : '';
  if (n.length <= 7) return `(${n.slice(0,2)}) ${n.slice(2)}`;
  return `(${n.slice(0,2)}) ${n.slice(2,7)}-${n.slice(7)}`;
};

const emptyForm = {
  remetente_nome: '', remetente_cpf: '', remetente_telefone: '',
  destinatario_nome: '', destinatario_cpf: '', destinatario_telefone: '',
  viagem_id: '', descricao: '', origem: '', destino: '',
  volumes: 1, codigo_interno: '', valor: '', forma_pagamento: 'Pix',
  status: 'recebida', observacoes: '',
};

const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[var(--brand-secondary)]";

export default function Encomendas() {
  const { hasRole } = useAuth();
  const canWrite = !hasRole('financeiro');

  const [encomendas, setEncomendas]               = useState([]);
  const [viagens, setViagens]                     = useState([]);
  const [search, setSearch]                       = useState('');
  const [statusFilter, setStatusFilter]           = useState('');
  const [loading, setLoading]                     = useState(false);
  const [modalOpen, setModalOpen]                 = useState(false);
  const [detailModal, setDetailModal]             = useState(null);
  const [editingId, setEditingId]                 = useState(null);
  const [deleteId, setDeleteId]                   = useState(null);
  const [etiquetaModal, setEtiquetaModal]         = useState(null);
  const [tamanhoEtiqueta, setTamanhoEtiqueta]     = useState('a4');
  const [transferEnc, setTransferEnc]             = useState(null);
  const [transferEncViagemId, setTransferEncViagemId] = useState('');
  const [savingTransferEnc, setSavingTransferEnc] = useState(false);
  const [form, setForm]                           = useState(emptyForm);
  const [saving, setSaving]                       = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [eRes, vRes] = await Promise.all([
        api.get('/encomendas', { params: { busca: search || undefined, status: statusFilter || undefined } }),
        api.get('/viagens'),
      ]);
      setEncomendas(eRes.data);
      setViagens(vRes.data);
    } catch { toast.error('Erro ao carregar dados'); }
    finally { setLoading(false); }
  }, [search, statusFilter]);

  useEffect(() => { fetchAll(); }, [statusFilter]);

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setModalOpen(true); };
  const openEdit = (e) => {
    setEditingId(e.id);
    setForm({
      remetente_nome: e.remetente_nome || '',
      remetente_cpf: e.remetente_cpf || '',
      remetente_telefone: e.remetente_telefone || '',
      destinatario_nome: e.destinatario_nome || '',
      destinatario_cpf: e.destinatario_cpf || '',
      destinatario_telefone: e.destinatario_telefone || '',
      viagem_id: e.viagem_id || '',
      descricao: e.descricao || '',
      origem: e.origem || '',
      destino: e.destino || '',
      volumes: e.volumes || 1,
      codigo_interno: e.codigo_interno || '',
      valor: e.valor != null ? String(e.valor) : '',
      forma_pagamento: e.forma_pagamento || 'Pix',
      status: e.status || 'recebida',
      observacoes: e.observacoes || '',
    });
    setModalOpen(true);
  };
  const closeModal = () => { setModalOpen(false); setEditingId(null); setForm(emptyForm); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        volumes: parseInt(form.volumes),
        codigo_interno: form.codigo_interno || null,
        valor: form.valor ? parseFloat(form.valor) : 0,
      };
      if (editingId) {
        await api.put(`/encomendas/${editingId}`, {
          descricao: payload.descricao, volumes: payload.volumes, status: payload.status,
          observacoes: payload.observacoes,
          remetente_nome: payload.remetente_nome, remetente_cpf: payload.remetente_cpf, remetente_telefone: payload.remetente_telefone,
          destinatario_nome: payload.destinatario_nome, destinatario_cpf: payload.destinatario_cpf, destinatario_telefone: payload.destinatario_telefone,
          valor: payload.valor, forma_pagamento: payload.forma_pagamento,
          origem: payload.origem, destino: payload.destino,
        });
        toast.success('Encomenda atualizada!');
      } else {
        await api.post('/encomendas', payload);
        toast.success('Encomenda registrada com sucesso!');
      }
      closeModal();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar encomenda');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/encomendas/${deleteId}`);
      toast.success('Encomenda excluída!');
      setDeleteId(null);
      fetchAll();
    } catch { toast.error('Erro ao excluir encomenda'); }
  };

  const handleTransferEncomenda = async () => {
    if (!transferEncViagemId) { toast.error('Selecione a viagem de destino'); return; }
    setSavingTransferEnc(true);
    try {
      await api.post(`/encomendas/${transferEnc.id}/transferir`, { nova_viagem_id: transferEncViagemId });
      toast.success('Encomenda transferida com sucesso!');
      setTransferEnc(null); setTransferEncViagemId('');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao transferir encomenda');
    } finally { setSavingTransferEnc(false); }
  };

  const handleGerarEtiqueta = async () => {
    if (!etiquetaModal) return;
    try {
      const res = await api.get(`/encomendas/${etiquetaModal.id}/etiqueta/pdf`, {
        params: { tamanho: tamanhoEtiqueta },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      toast.success('Etiqueta gerada! Imprima pela janela que abriu.');
      setEtiquetaModal(null);
    } catch { toast.error('Erro ao gerar etiqueta'); }
  };

  const f = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div className="space-y-5" data-testid="encomendas-page">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Encomendas</h1>
          <p className="text-slate-500 text-sm mt-0.5">{encomendas.length} encomenda(s) registrada(s)</p>
        </div>
        {canWrite && (
          <button onClick={openCreate} data-testid="btn-nova-encomenda"
            className="flex items-center gap-2 text-white font-semibold px-4 py-2.5 rounded-xl text-sm transition-colors shadow-sm"
            style={{ backgroundColor: 'var(--brand-primary)' }}>
            <Plus className="w-4 h-4" /> Nova Encomenda
          </button>
        )}
      </div>

      {/* ── Filtros ── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchAll()}
              placeholder="Buscar por descrição ou código..." data-testid="encomendas-search-input"
              className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[var(--brand-secondary)]" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="encomendas-status-filter"
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none bg-white text-slate-700 min-w-[160px]">
            <option value="">Todos os status</option>
            {STATUS_ENC.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <button onClick={fetchAll} data-testid="btn-buscar-encomendas"
            className="px-5 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-sm font-semibold transition-colors">
            Buscar
          </button>
        </div>
      </div>

      {/* ── Tabela ── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-20 text-slate-400">
            <div className="animate-spin w-6 h-6 border-2 border-t-transparent rounded-full mr-3"
              style={{ borderColor: 'var(--brand-primary)', borderTopColor: 'transparent' }} />
            Carregando...
          </div>
        ) : encomendas.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">Nenhuma encomenda encontrada</p>
            <p className="text-slate-400 text-sm mt-1">Registre uma encomenda para começar</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Código</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Descrição</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Remetente</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Viagem</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {encomendas.map((e) => (
                  <tr key={e.id} className="hover:bg-slate-50/60 transition-colors" data-testid={`encomenda-row-${e.id}`}>
                    <td className="px-5 py-3.5">
                      <span className="font-mono text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded font-semibold">
                        {e.codigo_interno || e.id?.slice(0,8)}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <p className="font-semibold text-slate-800 text-sm">{e.descricao}</p>
                      <p className="text-xs text-slate-400">{e.volumes} volume(s)</p>
                    </td>
                    <td className="px-5 py-3.5 hidden md:table-cell">
                      <p className="text-sm text-slate-700">{e.remetente_nome || '—'}</p>
                    </td>
                    <td className="px-5 py-3.5 hidden lg:table-cell">
                      {e.viagem ? (
                        <div>
                          <p className="text-xs font-medium text-slate-700">{e.viagem.titulo}</p>
                          <div className="flex items-center gap-1 text-slate-400 text-xs mt-0.5">
                            <MapPin className="w-3 h-3" />
                            <span>{e.viagem.origem} → {e.viagem.destino}</span>
                          </div>
                        </div>
                      ) : '—'}
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${getStatusCls(e.status)}`}>
                        {getStatusLabel(e.status)}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => { setEtiquetaModal(e); setTamanhoEtiqueta('a4'); }}
                          title="Imprimir etiqueta" data-testid={`btn-etiqueta-${e.id}`}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors">
                          <Printer className="w-4 h-4" />
                        </button>
                        <button onClick={() => setDetailModal(e)}
                          title="Ver QR Code" data-testid={`btn-view-encomenda-${e.id}`}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-orange-600 hover:bg-orange-50 transition-colors">
                          <QrCode className="w-4 h-4" />
                        </button>
                        {canWrite && (
                          <>
                            <button onClick={() => openEdit(e)} title="Editar"
                              data-testid={`btn-edit-encomenda-${e.id}`}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button onClick={() => { setTransferEnc(e); setTransferEncViagemId(''); }}
                              title="Transferir para outra viagem" data-testid={`btn-transfer-encomenda-${e.id}`}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-purple-600 hover:bg-purple-50 transition-colors">
                              <ArrowLeftRight className="w-4 h-4" />
                            </button>
                            <button onClick={() => setDeleteId(e.id)} title="Excluir"
                              data-testid={`btn-delete-encomenda-${e.id}`}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Modal Criar / Editar ── */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="encomenda-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="font-bold text-slate-800 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {editingId ? 'Editar Encomenda' : 'Nova Encomenda'}
              </h2>
              <button onClick={closeModal} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">

              {/* Dados do Remetente */}
              <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/60 space-y-3">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Dados do Remetente</p>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Nome do Remetente *</label>
                  <input value={form.remetente_nome} onChange={(e) => f('remetente_nome', e.target.value)} required
                    placeholder="Nome completo" className={inp} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">CPF / CNPJ do Remetente *</label>
                    <input value={form.remetente_cpf}
                      onChange={(e) => f('remetente_cpf', maskCpfCnpj(e.target.value))} required
                      placeholder="CPF ou CNPJ" className={inp} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Telefone do Remetente *</label>
                    <input value={form.remetente_telefone}
                      onChange={(e) => f('remetente_telefone', maskTel(e.target.value))} required
                      placeholder="(00) 00000-0000" className={inp} />
                  </div>
                </div>
              </div>

              {/* Dados do Destinatário */}
              <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/60 space-y-3">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Dados do Destinatário</p>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Nome do Destinatário *</label>
                  <input value={form.destinatario_nome} onChange={(e) => f('destinatario_nome', e.target.value)} required
                    placeholder="Nome completo" className={inp} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">CPF / CNPJ do Destinatário</label>
                    <input value={form.destinatario_cpf}
                      onChange={(e) => f('destinatario_cpf', maskCpfCnpj(e.target.value))}
                      placeholder="CPF ou CNPJ" className={inp} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Telefone do Destinatário *</label>
                    <input value={form.destinatario_telefone}
                      onChange={(e) => f('destinatario_telefone', maskTel(e.target.value))} required
                      placeholder="(00) 00000-0000" className={inp} />
                  </div>
                </div>
              </div>

              {/* Viagem */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Viagem *</label>
                <select value={form.viagem_id} onChange={(e) => f('viagem_id', e.target.value)} required
                  className={`${inp} bg-white`}>
                  <option value="">Selecione a viagem</option>
                  {viagens.filter(v => v.status !== 'cancelada').map(v => (
                    <option key={v.id} value={v.id}>
                      {v.titulo} — {new Date(v.data + 'T00:00:00').toLocaleDateString('pt-BR')}
                    </option>
                  ))}
                </select>
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Descrição da encomenda *</label>
                <input value={form.descricao} onChange={(e) => f('descricao', e.target.value)} required
                  placeholder="Ex: Caixas, moto, envelope..." className={inp} />
              </div>

              {/* Origem / Destino */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Origem (Local de Coleta)</label>
                  <input value={form.origem} onChange={(e) => f('origem', e.target.value)}
                    placeholder="Ex: Rua das Flores, 100 - SP" className={inp} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Destino (Local de Entrega)</label>
                  <input value={form.destino} onChange={(e) => f('destino', e.target.value)}
                    placeholder="Ex: Av. Brasil, 500 - Campinas" className={inp} />
                </div>
              </div>

              {/* Volumes / Código */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Qtd. Volumes</label>
                  <input type="number" min="1" value={form.volumes}
                    onChange={(e) => f('volumes', e.target.value)} className={inp} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Código Interno</label>
                  <input value={form.codigo_interno} onChange={(e) => f('codigo_interno', e.target.value)}
                    placeholder="Auto-gerado se vazio" className={inp} />
                </div>
              </div>

              {/* Valor / Pagamento */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Valor da Encomenda (R$) *</label>
                  <input type="number" step="0.01" min="0" value={form.valor}
                    onChange={(e) => f('valor', e.target.value)} required placeholder="0,00" className={inp} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Forma de Pagamento *</label>
                  <select value={form.forma_pagamento} onChange={(e) => f('forma_pagamento', e.target.value)} required
                    className={`${inp} bg-white`}>
                    {FORMAS_PAGAMENTO_ENC.map(fp => <option key={fp} value={fp}>{fp}</option>)}
                  </select>
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Status</label>
                <select value={form.status} onChange={(e) => f('status', e.target.value)} className={`${inp} bg-white`}>
                  {STATUS_ENC.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>

              {/* Observações */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Observações</label>
                <textarea value={form.observacoes} onChange={(e) => f('observacoes', e.target.value)}
                  rows={2} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[var(--brand-secondary)] resize-none" />
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeModal}
                  className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50">
                  Cancelar
                </button>
                <button type="submit" disabled={saving} data-testid="btn-salvar-encomenda"
                  className="flex-1 text-white rounded-lg text-sm font-semibold py-2.5 disabled:opacity-60"
                  style={{ backgroundColor: 'var(--brand-primary)' }}>
                  {saving ? 'Salvando...' : (editingId ? 'Atualizar' : 'Registrar Encomenda')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal QR Code / Detalhes ── */}
      {detailModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="encomenda-detail-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <div>
                <h2 className="font-bold text-slate-800 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>Detalhes da Encomenda</h2>
                <p className="text-slate-400 text-xs mt-0.5 font-mono">{detailModal.codigo_interno}</p>
              </div>
              <button onClick={() => setDetailModal(null)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {detailModal.qrcode && (
                <div className="flex flex-col items-center bg-slate-50 rounded-xl p-6 border border-slate-200">
                  <img src={detailModal.qrcode} alt="QR Code" className="w-40 h-40" data-testid="encomenda-qrcode" />
                  <p className="text-xs text-slate-500 mt-2 font-mono">{detailModal.codigo_interno}</p>
                </div>
              )}
              <div className="flex items-center justify-between bg-white border border-slate-200 rounded-lg p-3">
                <span className="text-sm text-slate-500">Status atual</span>
                <span className={`text-xs px-2 py-1 rounded-full font-semibold ${getStatusCls(detailModal.status)}`}>
                  {getStatusLabel(detailModal.status)}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-400 mb-1">Remetente</p>
                  <p className="font-medium text-slate-800">{detailModal.remetente_nome || '—'}</p>
                  {detailModal.remetente_cpf && <p className="text-xs text-slate-500 mt-0.5">CPF/CNPJ: {detailModal.remetente_cpf}</p>}
                  {detailModal.remetente_telefone && <p className="text-xs text-slate-500">Tel: {detailModal.remetente_telefone}</p>}
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-400 mb-1">Destinatário</p>
                  <p className="font-medium text-slate-800">{detailModal.destinatario_nome || '—'}</p>
                  {detailModal.destinatario_cpf && <p className="text-xs text-slate-500 mt-0.5">CPF/CNPJ: {detailModal.destinatario_cpf}</p>}
                  {detailModal.destinatario_telefone && <p className="text-xs text-slate-500">Tel: {detailModal.destinatario_telefone}</p>}
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-400 mb-1">Volumes</p>
                  <p className="font-medium text-slate-800">{detailModal.volumes}</p>
                </div>
                {detailModal.valor != null && (
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-1">Valor / Pagamento</p>
                    <p className="font-medium text-slate-800">R$ {Number(detailModal.valor).toFixed(2)}</p>
                    {detailModal.forma_pagamento && <p className="text-xs text-slate-500 mt-0.5">{detailModal.forma_pagamento}</p>}
                  </div>
                )}
                {(detailModal.origem || detailModal.destino) && (
                  <div className="bg-slate-50 rounded-lg p-3 sm:col-span-2">
                    <p className="text-xs text-slate-400 mb-1 flex items-center gap-1"><MapPin className="w-3 h-3"/>Rota da Encomenda</p>
                    <div className="flex items-center gap-2 text-sm text-slate-700 font-medium">
                      <span>{detailModal.origem || '—'}</span>
                      <span className="text-slate-400">→</span>
                      <span>{detailModal.destino || '—'}</span>
                    </div>
                  </div>
                )}
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-400 mb-1">Viagem</p>
                  {detailModal.viagem ? (
                    <div>
                      <p className="font-medium text-slate-800 text-xs">{detailModal.viagem.titulo}</p>
                      <div className="flex items-center gap-1 text-slate-400 text-xs mt-0.5">
                        <MapPin className="w-3 h-3" />
                        <span>{detailModal.viagem.origem} → {detailModal.viagem.destino}</span>
                      </div>
                    </div>
                  ) : '—'}
                </div>
              </div>
              {detailModal.descricao && (
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-400 mb-1">Descrição</p>
                  <p className="text-sm text-slate-700">{detailModal.descricao}</p>
                </div>
              )}
              {detailModal.status === 'entregue' && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-2">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                    <p className="font-semibold text-emerald-800 text-sm">Entrega confirmada</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-emerald-700">
                    {detailModal.data_entrega && (
                      <p><span className="text-emerald-500">Data/hora: </span>{new Date(detailModal.data_entrega).toLocaleString('pt-BR')}</p>
                    )}
                    {detailModal.nome_recebedor && (
                      <p className="sm:col-span-2"><span className="text-emerald-500">Recebeu: </span>
                        <span className="font-semibold">{detailModal.nome_recebedor}</span></p>
                    )}
                  </div>
                </div>
              )}

              {/* Linha do tempo */}
              {detailModal.historico?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-700 text-sm mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-slate-400" /> Linha do Tempo
                  </h3>
                  <div className="relative">
                    <div className="absolute left-[13px] top-2 bottom-2 w-0.5 bg-slate-200" />
                    <div className="space-y-3">
                      {[...detailModal.historico].reverse().map((h, i) => {
                        const isLast = i === 0;
                        const icons = {
                          recebida:    { icon: Archive,     bg: 'bg-blue-100',    ic: 'text-blue-500' },
                          em_transito: { icon: Truck,       bg: 'bg-amber-100',   ic: 'text-amber-500' },
                          entregue:    { icon: CheckCircle, bg: 'bg-emerald-100', ic: 'text-emerald-600' },
                          cancelada:   { icon: XCircle,     bg: 'bg-red-100',     ic: 'text-red-500' },
                        };
                        const { icon: Icon, bg, ic } = icons[h.status] || { icon: Clock, bg: 'bg-slate-100', ic: 'text-slate-400' };
                        return (
                          <div key={i} className="flex items-start gap-3 relative">
                            <div className={`w-7 h-7 rounded-full ${bg} flex items-center justify-center flex-shrink-0 z-10 ${isLast ? 'ring-2 ring-offset-1 ring-orange-400' : ''}`}>
                              <Icon className={`w-3.5 h-3.5 ${ic}`} />
                            </div>
                            <div className={`flex-1 pb-1 ${isLast ? '' : 'opacity-70'}`}>
                              <div className="flex items-center justify-between gap-2 flex-wrap">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${getStatusCls(h.status)}`}>
                                  {getStatusLabel(h.status)}
                                </span>
                                {h.data && (
                                  <span className="text-xs text-slate-400 whitespace-nowrap">
                                    {new Date(h.data).toLocaleString('pt-BR')}
                                  </span>
                                )}
                              </div>
                              {h.usuario && (
                                <p className="text-xs text-slate-500 mt-0.5">por <span className="font-medium text-slate-600">{h.usuario}</span></p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Transferir ── */}
      {transferEnc && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="font-bold text-slate-800 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>Transferir Encomenda</h2>
                <p className="text-sm text-slate-500 mt-0.5">{transferEnc.remetente_nome} → {transferEnc.destinatario_nome}</p>
              </div>
              <button onClick={() => { setTransferEnc(null); setTransferEncViagemId(''); }}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Viagem atual</p>
                <p className="text-sm font-semibold text-slate-800">{transferEnc.viagem?.titulo || '—'}</p>
                <p className="text-xs text-slate-400">{transferEnc.viagem?.origem} → {transferEnc.viagem?.destino}</p>
              </div>
              <div className="flex justify-center">
                <div className="flex items-center gap-2 text-purple-500">
                  <ArrowLeftRight className="w-5 h-5" />
                  <span className="text-sm font-medium">Transferir para</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Nova Viagem *</label>
                <select value={transferEncViagemId} onChange={(e) => setTransferEncViagemId(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400/20 focus:border-purple-400 bg-white">
                  <option value="">Selecione a viagem de destino</option>
                  {viagens.filter(v => v.id !== transferEnc.viagem_id).map(v => (
                    <option key={v.id} value={v.id}>
                      {v.titulo} — {new Date(v.data + 'T00:00:00').toLocaleDateString('pt-BR')}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => { setTransferEnc(null); setTransferEncViagemId(''); }}
                  className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50">
                  Cancelar
                </button>
                <button type="button" onClick={handleTransferEncomenda} disabled={savingTransferEnc}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold py-2.5 disabled:opacity-60 flex items-center justify-center gap-2">
                  <ArrowLeftRight className="w-4 h-4" />
                  {savingTransferEnc ? 'Transferindo...' : 'Confirmar Transferência'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirmar Exclusão ── */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="font-bold text-slate-800 text-lg mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Confirmar exclusão</h3>
            <p className="text-slate-500 text-sm mb-5">Deseja excluir esta encomenda?</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)}
                className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium">
                Cancelar
              </button>
              <button onClick={handleDelete} data-testid="btn-confirm-delete-encomenda"
                className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-semibold py-2.5">
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Etiqueta ── */}
      {etiquetaModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="etiqueta-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Printer className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <h2 className="font-bold text-slate-800 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>Imprimir Etiqueta</h2>
                  <p className="text-slate-500 text-xs">{etiquetaModal.codigo_interno}</p>
                </div>
              </div>
              <button onClick={() => setEtiquetaModal(null)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-slate-600 font-medium">Selecione o tamanho da etiqueta:</p>
              {TAMANHOS_ETIQUETA.map((t) => (
                <label key={t.value}
                  className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${tamanhoEtiqueta === t.value ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}>
                  <input type="radio" name="tamanho-enc" value={t.value} checked={tamanhoEtiqueta === t.value}
                    onChange={() => setTamanhoEtiqueta(t.value)} className="mt-0.5 accent-emerald-500" />
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">{t.label}</p>
                    <p className="text-slate-500 text-xs">{t.desc}</p>
                  </div>
                </label>
              ))}
              <p className="text-xs text-slate-400 mt-1">Todas com borda tracejada para recorte. QR Code incluso.</p>
            </div>
            <div className="flex gap-3 px-5 pb-5">
              <button onClick={() => setEtiquetaModal(null)}
                className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium">
                Cancelar
              </button>
              <button onClick={handleGerarEtiqueta} data-testid="btn-gerar-etiqueta"
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-semibold py-2.5 flex items-center justify-center gap-2">
                <Printer className="w-4 h-4" /> Gerar e Abrir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
